import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { useNotificationSettings } from '../../hooks/useNotificationSettings';
import { ScheduledNotificationSettings } from '../../services/NotificationSettingsService';

const presets = [
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Only the most important notifications',
    icon: '🔕',
    preference: 'Low frequency',
    settings: {
      enabled: true,
      preferred_time: '18:00',
      timezone: 'auto',
      quiet_hours: {
        enabled: false,
        start: '22:00',
        end: '08:00'
      },
      frequency_preferences: {
        daily: false,
        weekly: true,
        monthly: false,
        quarterly: false
      },
      notification_types: {
        documentation_reminders: false,
        progress_summaries: false,
        achievement_celebrations: true,
        goal_setting_prompts: false,
        reflection_reminders: false,
        self_review: false,
        career_opportunities: false,
        milestone_celebrations: true,
        streak_reminders: false
      },
      notification_frequencies: {
        documentation_reminders: 'weekly_end',
        progress_summaries: 'weekly',
        achievement_celebrations: 'immediate',
        goal_setting_prompts: 'weekly',
        reflection_reminders: 'weekly',
        self_review: 'quarterly',
        career_opportunities: 'quarterly',
        milestone_celebrations: 'immediate',
        streak_reminders: 'daily'
      }
    } as ScheduledNotificationSettings
  },
  {
    id: 'balanced',
    name: 'Balanced',
    description: 'A thoughtful mix of reminders and celebrations',
    icon: '⚖️',
    preference: 'Moderate frequency',
    settings: {
      enabled: true,
      preferred_time: '18:00',
      timezone: 'auto',
      quiet_hours: {
        enabled: false,
        start: '22:00',
        end: '08:00'
      },
      frequency_preferences: {
        daily: false,
        weekly: true,
        monthly: true,
        quarterly: false
      },
      notification_types: {
        documentation_reminders: false,
        progress_summaries: true,
        achievement_celebrations: true,
        goal_setting_prompts: false,
        reflection_reminders: false,
        self_review: true,
        career_opportunities: false,
        milestone_celebrations: true,
        streak_reminders: false
      },
      notification_frequencies: {
        documentation_reminders: 'weekly_end',
        progress_summaries: 'weekly',
        achievement_celebrations: 'immediate',
        goal_setting_prompts: 'weekly',
        reflection_reminders: 'weekly',
        self_review: 'quarterly',
        career_opportunities: 'quarterly',
        milestone_celebrations: 'immediate',
        streak_reminders: 'daily'
      }
    } as ScheduledNotificationSettings
  },
  {
    id: 'comprehensive',
    name: 'Comprehensive',
    description: 'Stay fully engaged with regular guidance',
    icon: '🔔',
    preference: 'Higher frequency',
    settings: {
      enabled: true,
      preferred_time: '18:00',
      timezone: 'auto',
      quiet_hours: {
        enabled: true,
        start: '22:00',
        end: '08:00'
      },
      frequency_preferences: {
        daily: true,
        weekly: true,
        monthly: true,
        quarterly: true
      },
      notification_types: {
        documentation_reminders: true,
        progress_summaries: true,
        achievement_celebrations: true,
        goal_setting_prompts: true,
        reflection_reminders: true,
        self_review: true,
        career_opportunities: false,
        milestone_celebrations: true,
        streak_reminders: true
      },
      notification_frequencies: {
        documentation_reminders: 'weekly_end',
        progress_summaries: 'weekly',
        achievement_celebrations: 'immediate',
        goal_setting_prompts: 'weekly',
        reflection_reminders: 'weekly',
        self_review: 'quarterly',
        career_opportunities: 'quarterly',
        milestone_celebrations: 'immediate',
        streak_reminders: 'daily'
      }
    } as ScheduledNotificationSettings
  }
];

interface NotificationPresetsProps {
  className?: string;
  onPresetApplied?: () => void;
}

const NotificationPresets: React.FC<NotificationPresetsProps> = ({ 
  className = '', 
  onPresetApplied 
}) => {
  const { updateScheduledSettings, isLoading, refreshSettings, settings, clearCache } = useNotificationSettings();
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePresetSelect = async (preset: typeof presets[0]) => {
    try {
      setSelectedPreset(preset.id);
      setError(null);
      console.log('Applying preset:', preset.name);
      console.log('Current settings before:', settings?.scheduled_notifications?.notification_types);
      console.log('Preset settings:', preset.settings.notification_types);
      
      // Apply the preset settings
      await updateScheduledSettings(preset.settings);
      
      // Clear cache and force multiple refreshes to ensure state is updated
      clearCache();
      await refreshSettings();
      
      // Wait a bit longer for React to process the state updates
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Force another refresh
      await refreshSettings();
      
      console.log('Preset applied successfully');
      
      // Force immediate UI refresh
      onPresetApplied?.();
      
      // Wait a bit more and check the final state
      setTimeout(() => {
        console.log('Settings after update:', settings?.scheduled_notifications?.notification_types);
      }, 300);
      
      // Clear selection after showing success
      setTimeout(() => setSelectedPreset(null), 1000);
    } catch (error) {
      console.error('Error applying preset:', error);
      setError(error instanceof Error ? error.message : 'Failed to apply preset');
      setSelectedPreset(null);
    }
  };

  return (
    <div className={className}>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Choose a preset for your active alerts. These control which notifications actively alert you with badges, banners, and your chosen delivery methods. 
        Disabled notifications will still appear in your "All" notifications tab at the lowest frequency. 
        Critical notifications (security, onboarding) always bypass these settings.
      </p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {presets.map(preset => {
          const isApplying = selectedPreset === preset.id;
          return (
            <button
              key={preset.id}
              onClick={() => handlePresetSelect(preset)}
              disabled={isLoading || isApplying}
              className={`p-4 border rounded-lg text-left transition-colors ${
                isApplying 
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'
              } ${
                isLoading || isApplying ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-2">{preset.icon}</span>
                <div className="font-medium text-gray-900 dark:text-white">
                  {preset.name}
                  {isApplying && <span className="ml-2 text-green-600 dark:text-green-400">✓</span>}
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {preset.description}
              </div>
              
              {/* Preview of what's enabled */}
              <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                <div>
                  Preference: {preset.preference}
                </div>
                <div>
                  Types enabled: {Object.values(preset.settings.notification_types).filter(Boolean).length}/9
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationPresets; 